import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Plus,
  X,
  Eye,
  EyeOff,
  Settings,
  PencilLine,
  BarChart3,
  Table,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Grid3X3,
  Activity,
  BarChart,
  Calculator,
  List
} from 'lucide-react';
import type { ChartData, ChartStudy } from '../../store/chartAtoms'; // Assuming types are here
import type { User } from 'firebase/auth'; // For currentUser prop
import type { Organization } from '../../store/organizationAtoms'; // For currentOrganization prop
import type { UniversalChart } from '@shared/index'
// Removed chart conversion imports - using UniversalChart directly everywhere
import SlidingToggle from '../ui/SlidingToggle';
import { CHART_TYPE_CONFIG } from '../../lib/chartUtils'; // Corrected import
import type { ChartType, Chart as FullChart, ChartColor, ChartConfiguration } from '@shared/schemas/chart'; // Added ChartConfiguration
import ChartAxisSettingsDialog from './ChartAxisSettingsDialog'; // Import the new dialog
import type { AxisOptions } from '../../store/chartAtoms'; // Import AxisOptions type
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import ChartColorPaletteChanger from '../ui/ChartColorPaletteChanger'; // Import color picker
import { useCustomPalettes } from '../../contexts/ChartPreferencesContext'; // Import custom palettes hook
import { DARK_BACKGROUND_COLORS } from '../../lib/themeAwareColors'; // Import default colors
import EditableChartTitle from './EditableChartTitle'; // Import editable title component
import ChartDataEditorDialog from './ChartDataEditorDialog'; // Import data editor dialog
import UniversalChartRenderer from './UniversalChartRenderer'; // Import the updated renderer
import { DataTableViewer } from '../ui/DataTableViewer'; // Import data table viewer

// Constants that might be passed or defined locally if not shared
const STUDY_COLORS = DARK_BACKGROUND_COLORS.slice(0, 6); // Use theme-aware colors
const MOVING_AVERAGE_PRESETS = [
  { label: '7 Period', value: 7 },
  { label: '14 Period', value: 14 },
  { label: '30 Period', value: 30 },
  { label: '52 Period', value: 52 },
];
const POLYNOMIAL_TREND_PRESETS = [
  { label: '2nd Degree', value: 2 },
  { label: '3rd Degree', value: 3 },
  { label: '4th Degree', value: 4 },
  { label: '5th Degree', value: 5 },
];
const EXPONENTIAL_SMOOTHING_PRESETS = [
  { label: 'Light Smoothing (α=0.1)', value: 0.1 },
  { label: 'Medium Smoothing (α=0.3)', value: 0.3 },
  { label: 'Heavy Smoothing (α=0.5)', value: 0.5 },
  { label: 'Aggressive Smoothing (α=0.7)', value: 0.7 },
];

interface ChartStudiesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  chart: UniversalChart; // Simplified - always use UniversalChart format
  isDatabaseChart: boolean; // This prop helps distinguish if needed, though chart structure can also tell
  initialStudies: ChartStudy[];
  onApplyStudies: (
    updatedStudies: ChartStudy[], 
    newChartType: ChartType, 
    newAxisOptions: AxisOptions | null, 
    newDisplayOptions: { showGridLines: boolean } | null,
    updatedConfiguration: Partial<ChartConfiguration>
  ) => Promise<void>;
  currentUser: User | null;
  currentOrganization: Organization | null;
  renderPreviewFunction: (chart: UniversalChart, studies: ChartStudy[]) => JSX.Element | null;
  // Add new props for edit functionality
  onChartUpdate?: () => void; // Callback when chart is updated
  onChartTitleUpdate?: (chartId: string, newTitle: string) => Promise<void>; // For title updates
  onChartPaletteChange?: (chartId: string, paletteId: string) => Promise<void>; // For palette changes
  onChartColorChange?: (chartId: string, color: ChartColor) => Promise<void>; // For single color changes
}

const ChartStudiesDialog: React.FC<ChartStudiesDialogProps> = ({
  isOpen,
  onClose,
  chart,
  isDatabaseChart,
  initialStudies,
  onApplyStudies,
  renderPreviewFunction,
  onChartUpdate,
  onChartTitleUpdate,
  onChartPaletteChange,
  onChartColorChange,
}) => {
  const [studies, setStudies] = useState<ChartStudy[]>([]);
  const [previewChartType, setPreviewChartType] = useState<ChartType>('bar'); // Default to bar
  const [isAxisSettingsDialogOpen, setIsAxisSettingsDialogOpen] = useState(false);
  const [previewAxisOptions, setPreviewAxisOptions] = useState<AxisOptions | null>(null);
  const [previewShowGridLines, setPreviewShowGridLines] = useState(true);
  const [previewShowLegend, setPreviewShowLegend] = useState(true);
  const [isDataEditorDialogOpen, setIsDataEditorDialogOpen] = useState(false);
  const [previewColorPalette, setPreviewColorPalette] = useState<string | null>(null);
  const [previewSingleColor, setPreviewSingleColor] = useState<ChartColor | null>(null);
  const [viewMode, setViewMode] = useState<'chart' | 'data'>('chart');
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
  const customPalettes = useCustomPalettes();

  useEffect(() => {
    setStudies(Array.isArray(initialStudies) ? initialStudies : []);
    if (isOpen && chart) {
      const initialType = (chart as FullChart).type || (chart as ChartData).chartType || 'bar';
      setPreviewChartType(initialType as ChartType);
      
      const existingOptions = (chart as FullChart).axisOptions || (chart as ChartData).axisOptions;
      setPreviewAxisOptions(existingOptions || null);
      
      const existingDisplayOptions = (chart as FullChart).displayOptions || (chart as ChartData).displayOptions;
      setPreviewShowGridLines(existingDisplayOptions?.showGridLines ?? true);
      
      // Initialize preview color states directly from the incoming chart's configuration
      // These will only be changed if the user interacts with the dialog's color pickers
      const config = chart.configuration || {};
      setPreviewSingleColor(config.singleColor || null); 
      setPreviewColorPalette(config.colorPalette || null); // Use null if not present
    }
  }, [initialStudies, isOpen, chart]); // Removed isDatabaseChart as chart.configuration is source of truth

  if (!isOpen || !chart) {
    return null;
  }

  const addStudy = (type: 'regression' | 'movingAverage' | 'polynomialTrend' | 'exponentialSmoothing' | 'mean' | 'median' | 'mode' | 'stdDeviation', period?: number, degree?: number, alpha?: number) => {
    let name = '';
    const studyConfigBase: Omit<ChartStudy, 'name'|'degree'|'period'|'alpha'> = {
      id: `study-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      color: STUDY_COLORS[studies.length % STUDY_COLORS.length],
      visible: true,
    };
    
    let newStudy: ChartStudy;

    switch (type) {
      case 'regression':
        name = 'Linear Regression';
        newStudy = { ...studyConfigBase, name, type };
        break;
      case 'movingAverage':
        name = `Moving Average (${period || 0})`;
        newStudy = { ...studyConfigBase, name, type, period: period || 0 };
        break;
      case 'polynomialTrend':
        name = `Polynomial Trend (${degree || 2}nd degree)`;
        newStudy = { ...studyConfigBase, name, type, degree: degree || 2 };
        break;
      case 'exponentialSmoothing':
        name = `Exponential Smoothing (α=${alpha || 0.3})`;
        newStudy = { ...studyConfigBase, name, type, alpha: alpha || 0.3 };
        break;
      case 'mean':
        name = 'Mean';
        newStudy = { ...studyConfigBase, name, type };
        break;
      case 'median':
        name = 'Median';
        newStudy = { ...studyConfigBase, name, type };
        break;
      case 'mode':
        name = 'Mode';
        newStudy = { ...studyConfigBase, name, type };
        break;
      case 'stdDeviation':
        name = 'Standard Deviation';
        newStudy = { ...studyConfigBase, name, type };
        break;
      default:
        // Should not happen
        console.error("Invalid study type");
        return;
    }
    
    setStudies(prev => [...prev, newStudy]);
  };

  const removeStudy = (studyId: string) => {
    setStudies(prev => prev.filter(study => study.id !== studyId));
  };

  const toggleStudyVisibility = (studyId: string) => {
    setStudies(prev => 
      prev.map(study => 
        study.id === studyId ? { ...study, visible: !study.visible } : study
      )
    );
  };

  // Add function to update study color
  const updateStudyColor = (studyId: string, color: ChartColor) => {
    setStudies(prev => 
      prev.map(study => 
        study.id === studyId ? { ...study, color } : study
      )
    );
  };

  // Handle edit data button click
  const handleEditDataClick = () => {
    if (isDatabaseChart) {
      setIsDataEditorDialogOpen(true);
    } else {
      // Show message for local charts (they need to be saved first)
      alert('To edit chart data, please save this chart to the database first by clicking the cloud upload icon.');
    }
  };

  // Handle chart title update
  const handleTitleUpdate = async (newTitle: string) => {
    if (onChartTitleUpdate && chart.id) {
      try {
        await onChartTitleUpdate(chart.id, newTitle);
      } catch (error) {
        console.error('Error updating chart title:', error);
        throw error;
      }
    }
  };

  const chartConfig = chart.configuration || {};
  const derivedInitialColor = previewSingleColor ?? chartConfig.singleColor;
  const derivedInitialPaletteId = previewColorPalette ?? chartConfig.colorPalette ?? (isDatabaseChart ? 'fireforge' : 'system-0');

  // Handle color/palette changes for the main chart preview
  const handleChartColorChange = async (color: ChartColor) => {
    setPreviewSingleColor(color);
    setPreviewColorPalette(null); // User picked a single color, so clear palette choice for preview
    if (onChartColorChange && chart.id) {
      try { await onChartColorChange(chart.id, color); } catch (e) { console.error('Update failed', e); }
    }
  };

  // This handler is now for when user explicitly picks a palette from the dialog's UI
  const handleChartPalettePreviewChange = async (paletteId: string) => {
    setPreviewColorPalette(paletteId);
    setPreviewSingleColor(null); // User picked a full palette, so clear single color choice for preview
    // Note: We are simplifying away from setting full palette IDs on the actual chart object.
    // This handler now primarily affects the *preview*.
    // If onChartPaletteChange is still wired up, it might need to be re-evaluated
    // in the context of the "single color only" simplification.
    // For now, if the parent Charts.tsx still has onChartPaletteChange, it points to a singleColor handler.
    if (onChartPaletteChange && chart.id) {
      // This call will ultimately set a singleColor based on the palette's default.
      try { await onChartPaletteChange(chart.id, paletteId); } catch (e) { console.error('Update failed', e); }
    }
  };
  
  // 🎯 SIMPLIFIED: Use the chart directly as UniversalChart - no more conversions!
  const universalChart: UniversalChart = chart as UniversalChart;

  // Apply preview modifications to the universal chart
  const baseConfig = universalChart.configuration || {};
  let previewChartConfiguration = { ...baseConfig };

  // If user interacted with dialog color pickers, those choices take precedence for the preview
  if (previewSingleColor !== null) {
    previewChartConfiguration.singleColor = previewSingleColor;
    delete previewChartConfiguration.colorPalette;
  } else if (previewColorPalette !== null) {
    previewChartConfiguration.colorPalette = previewColorPalette;
    delete previewChartConfiguration.singleColor;
  }

  // Create the preview chart with all updates (memoized to ensure re-render on studies change)
  const chartToRenderForPreview: UniversalChart = useMemo(() => ({
    ...universalChart,
    chartType: previewChartType,
    studies: studies, // 🎯 KEY FIX: Always include studies for moving averages!
    axisOptions: previewAxisOptions || undefined,
    displayOptions: { showGridLines: previewShowGridLines, showLegend: previewShowLegend },
    configuration: previewChartConfiguration || undefined,
  }), [universalChart, previewChartType, studies, previewAxisOptions, previewShowGridLines, previewShowLegend, previewChartConfiguration]);

  const selectedTypeDetails = CHART_TYPE_CONFIG.find(t => t.id === previewChartType);

  // Log the axisOptions being sent to the preview just before rendering
  console.log('[ChartStudiesDialog] chartToRenderForPreview.axisOptions for preview:', 
    chartToRenderForPreview.axisOptions ? JSON.parse(JSON.stringify(chartToRenderForPreview.axisOptions)) : null
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-6xl w-full mx-auto h-auto md:h-[80vh] flex flex-col md:flex-row overflow-hidden">
        {/* Left Panel - Studies Controls */}
        <div className={`${isLeftPanelCollapsed ? 'w-16' : 'w-full md:w-1/3'} md:border-r border-b md:border-b-0 border-border transition-all duration-300 overflow-y-auto`}>
          {isLeftPanelCollapsed ? (
            /* Collapsed Icon Bar */
            <div className="p-4 space-y-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsLeftPanelCollapsed(false)}
                title="Expand Studies Panel"
                className="w-8 h-8"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              
              {/* Chart Settings Icons */}
              <div className="space-y-2 border-b pb-3">
                <ChartColorPaletteChanger
                  className="w-8 h-8"
                  chartType={chart.type as ChartType}
                  currentColor={previewChartConfiguration.singleColor ?? undefined}
                  currentPaletteId={previewChartConfiguration.colorPalette ?? undefined}
                  onColorChange={handleChartColorChange}
                />
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setIsAxisSettingsDialogOpen(true)}
                  title="Chart Axis Settings"
                  className="w-8 h-8"
                >
                  <Settings className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setPreviewShowGridLines(!previewShowGridLines)}
                  title={previewShowGridLines ? "Hide Grid Lines" : "Show Grid Lines"}
                  className={`w-8 h-8 ${previewShowGridLines ? 'bg-orange-100 text-orange-600' : ''}`}
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setPreviewShowLegend(!previewShowLegend)}
                  title={previewShowLegend ? "Hide Legend" : "Show Legend"}
                  className={`w-8 h-8 ${previewShowLegend ? 'bg-orange-100 text-orange-600' : ''}`}
                >
                  {previewShowLegend ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </Button>
              </div>
              
              {/* Studies Icons */}
              <div className="space-y-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => addStudy('regression')}
                  title="Add Linear Regression"
                  className="w-8 h-8"
                >
                  <TrendingUp className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => addStudy('movingAverage', 7)}
                  title="Moving Averages"
                  className="w-8 h-8"
                >
                  <Activity className="h-4 w-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => addStudy('polynomialTrend', undefined, 2)}
                  title="Polynomial Trends"
                  className="w-8 h-8"
                >
                  <BarChart className="h-4 w-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => addStudy('exponentialSmoothing', undefined, undefined, 0.3)}
                  title="Exponential Smoothing"
                  className="w-8 h-8"
                >
                  <Activity className="h-4 w-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => addStudy('mean')}
                  title="Statistical Markers"
                  className="w-8 h-8"
                >
                  <Calculator className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Active Studies Count */}
              {studies.length > 0 && (
                <div className="text-center border-t pt-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-800">
                    {studies.length}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Expanded Panel */
            <div className="p-4">
              <div className="flex items-center justify-end mb-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsLeftPanelCollapsed(true)}
                  title="Collapse Studies Panel"
                  className="w-8 h-8"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="space-y-3">
              {/* Chart Controls in Expanded Mode */}
              <div>
                <div className="space-y-2 mb-3 border-b pb-3">
                  <div className="flex items-center gap-3">
                    <ChartColorPaletteChanger
                      className="h-8 w-8 flex-shrink-0"
                      chartType={chart.type as ChartType}
                      currentColor={previewChartConfiguration.singleColor ?? undefined}
                      currentPaletteId={previewChartConfiguration.colorPalette ?? undefined}
                      onColorChange={handleChartColorChange}
                    />
                    <span className="text-sm text-muted-foreground">Colors</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => setIsAxisSettingsDialogOpen(true)}
                      title="Chart Axis Settings"
                      className="h-8 w-8 flex-shrink-0"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">Axis Settings</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => setPreviewShowGridLines(!previewShowGridLines)}
                      title={previewShowGridLines ? "Hide Grid Lines" : "Show Grid Lines"}
                      className={`h-8 w-8 flex-shrink-0 ${previewShowGridLines ? 'bg-orange-100 text-orange-600' : ''}`}
                    >
                      <Grid3X3 className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">Show Grid</span>
                  </div>
                </div>
              </div>

            <div> {/* Main group for "Add Studies" options */}
              <div className="space-y-2">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-4 w-4 flex-shrink-0" />
                <Button onClick={() => addStudy('regression')} variant="outline" size="sm" className="flex-1">
                  <Plus className="h-4 w-4 mr-2" /> Linear Regression
                </Button>
              </div>

              <Accordion type="multiple" className="w-full">
                {/* Moving Average Section */}
                <AccordionItem value="moving-averages">
                  <AccordionTrigger className="text-sm font-medium text-muted-foreground py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      Moving Averages
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2 pb-0">
                    <div className="space-y-1">
                      {MOVING_AVERAGE_PRESETS.map((preset) => (
                        <Button key={preset.value} onClick={() => addStudy('movingAverage', preset.value)} variant="outline" size="sm" className="w-full">
                          <Plus className="h-4 w-4 mr-2" /> {preset.label}
                        </Button>
                      ))}
                      <div className="flex flex-col sm:flex-row gap-2 mt-2">
                        <input type="number" placeholder="Custom period" className="flex-1 px-2 py-1 text-sm border rounded sm:min-w-0 min-w-full"
                          onKeyDown={(e) => { if (e.key === 'Enter') { const period = parseInt((e.target as HTMLInputElement).value); if (period > 0) { addStudy('movingAverage', period); (e.target as HTMLInputElement).value = ''; } } }} />
                        <Button size="sm" className="sm:w-auto w-full"
                          onClick={(e) => { const input = (e.target as HTMLElement).closest('.flex')?.querySelector('input'); if (input) { const period = parseInt(input.value || '0'); if (period > 0) { addStudy('movingAverage', period); input.value = ''; } } }}>
                          Add
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Polynomial Trend Section */}
                <AccordionItem value="polynomial-trends">
                  <AccordionTrigger className="text-sm font-medium text-muted-foreground py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <BarChart className="h-4 w-4" />
                      Polynomial Trends
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2 pb-0">
                    <div className="space-y-1">
                      {POLYNOMIAL_TREND_PRESETS.map((preset) => (
                        <Button key={preset.value} onClick={() => addStudy('polynomialTrend', undefined, preset.value)} variant="outline" size="sm" className="w-full">
                          <Plus className="h-4 w-4 mr-2" /> {preset.label}
                        </Button>
                      ))}
                      <div className="flex flex-col sm:flex-row gap-2 mt-2">
                        <input type="number" min="2" max="10" placeholder="Custom degree" className="flex-1 px-2 py-1 text-sm border rounded sm:min-w-0 min-w-full"
                          onKeyDown={(e) => { if (e.key === 'Enter') { const degree = parseInt((e.target as HTMLInputElement).value); if (degree >= 2 && degree <= 10) { addStudy('polynomialTrend', undefined, degree); (e.target as HTMLInputElement).value = ''; } } }} />
                        <Button size="sm" className="sm:w-auto w-full"
                          onClick={(e) => { const input = (e.target as HTMLElement).closest('.flex')?.querySelector('input'); if (input) { const degree = parseInt(input.value || '0'); if (degree >= 2 && degree <= 10) { addStudy('polynomialTrend', undefined, degree); input.value = ''; } } }}>
                          Add
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Exponential Smoothing Section */}
                <AccordionItem value="exponential-smoothing">
                  <AccordionTrigger className="text-sm font-medium text-muted-foreground py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      Exponential Smoothing
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2 pb-0">
                    <div className="space-y-1">
                      {EXPONENTIAL_SMOOTHING_PRESETS.map((preset) => (
                        <Button key={preset.value} onClick={() => addStudy('exponentialSmoothing', undefined, undefined, preset.value)} variant="outline" size="sm" className="w-full">
                          <Plus className="h-4 w-4 mr-2" /> {preset.label}
                        </Button>
                      ))}
                      <div className="flex flex-col sm:flex-row gap-2 mt-2">
                        <input type="number" min="0.01" max="1" step="0.01" placeholder="Alpha (0.01-1)" className="flex-1 px-2 py-1 text-sm border rounded sm:min-w-0 min-w-full"
                          onKeyDown={(e) => { if (e.key === 'Enter') { const alpha = parseFloat((e.target as HTMLInputElement).value); if (alpha >= 0.01 && alpha <= 1) { addStudy('exponentialSmoothing', undefined, undefined, alpha); (e.target as HTMLInputElement).value = ''; } } }} />
                        <Button size="sm" className="sm:w-auto w-full"
                          onClick={(e) => { const input = (e.target as HTMLElement).closest('.flex')?.querySelector('input'); if (input) { const alpha = parseFloat(input.value || '0'); if (alpha >= 0.01 && alpha <= 1) { addStudy('exponentialSmoothing', undefined, undefined, alpha); input.value = ''; } } }}>
                          Add
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* NEW Statistical Markers Section */}
                <AccordionItem value="statistical-markers">
                  <AccordionTrigger className="text-sm font-medium text-muted-foreground py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Calculator className="h-4 w-4" />
                      Statistical Markers
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2 pb-0">
                    <div className="space-y-1">
                      <Button onClick={() => addStudy('mean')} variant="outline" size="sm" className="w-full">
                        <Plus className="h-4 w-4 mr-2" /> Add Mean Line
                      </Button>
                      <Button onClick={() => addStudy('median')} variant="outline" size="sm" className="w-full">
                        <Plus className="h-4 w-4 mr-2" /> Add Median Line
                      </Button>
                      <Button onClick={() => addStudy('mode')} variant="outline" size="sm" className="w-full">
                        <Plus className="h-4 w-4 mr-2" /> Add Mode Line
                      </Button>
                      <Button onClick={() => addStudy('stdDeviation')} variant="outline" size="sm" className="w-full">
                        <Plus className="h-4 w-4 mr-2" /> Add Std. Deviation Area
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              </div>
            </div> {/* End of main group for "Add Studies" options */}

            {/* Warning for insufficient data */}
            {(() => {
              if (!chart) return null;
              let dataLength = 0;
              const isDb = (chart as FullChart).datasets;
              if (isDb && (chart as FullChart).datasets?.[0]?.data) {
                dataLength = (chart as FullChart).datasets[0].data.length;
              } else if (!isDb && (chart as ChartData).data) {
                dataLength = (chart as ChartData).data.length;
              }
              
              const insufficientMAs = studies.filter(s => s.type === 'movingAverage' && s.visible && s.period && dataLength < s.period).map(s => s.period);
              const insufficientPolynomials = studies.filter(s => s.type === 'polynomialTrend' && s.visible && s.degree && dataLength < (s.degree + 1)).map(s => `${s.degree}°`);

              let warnings = [];
              if (insufficientMAs.length > 0) {
                warnings.push(`Moving Average(s): ${insufficientMAs.join(', ')}`);
              }
              if (insufficientPolynomials.length > 0) {
                warnings.push(`Polynomial Trend(s): ${insufficientPolynomials.join(', ')}`);
              }

              if (warnings.length === 0) return null;
              
              return (
                <div className="mt-4 p-3 rounded bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
                  Not enough data points for: {warnings.join('; ')}. Add more data or choose smaller periods/degrees.
                </div>
              );
            })()}
              </div>

              {/* Active Studies at the bottom */}
              {studies.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h4 className="font-medium mb-2">Active Studies</h4>
                  <div className="space-y-2">
                    {studies.map((study) => (
                      <div key={study.id} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                        <ChartColorPaletteChanger
                          className="h-6 w-6 shrink-0"
                          chartType="line"
                          currentColor={study.color}
                          onColorChange={(newColor) => updateStudyColor(study.id, newColor)}
                        />
                        <span className="flex-1 text-sm truncate">{study.name}</span>
                        <Button variant="ghost" size="icon" onClick={() => toggleStudyVisibility(study.id)} className="h-7 w-7 p-0 shrink-0" title={study.visible ? "Hide Study" : "Show Study"}>
                          {study.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => removeStudy(study.id)} className="h-7 w-7 p-0 text-destructive shrink-0" title="Remove Study">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Panel - Preview */}
        <div className="flex-1 p-6 flex flex-col overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-lg font-semibold">Preview:</span>
              <EditableChartTitle
                title={chart.title || 'Untitled Chart'}
                onSave={handleTitleUpdate}
                className="text-lg font-semibold flex-1"
              />
            </div>
            <div className="flex items-center gap-3">
              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <Button
                  variant={viewMode === 'chart' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('chart')}
                  className={`flex items-center gap-2 px-3 py-1.5 ${
                    viewMode === 'chart' 
                      ? 'bg-white shadow-sm text-gray-900' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <BarChart3 className="h-4 w-4" />
                  Chart
                </Button>
                <Button
                  variant={viewMode === 'data' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('data')}
                  className={`flex items-center gap-2 px-3 py-1.5 ${
                    viewMode === 'data' 
                      ? 'bg-white shadow-sm text-gray-900' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Table className="h-4 w-4" />
                  Data
                </Button>
              </div>
              {/* Buttons moved to bottom right */}
            </div>
          </div>

          {/* Chart Type Sliding Toggle */}
          <div className="shrink-0 mb-3">
            <SlidingToggle
              options={CHART_TYPE_CONFIG.map(ct => {
                const IconComponent = ct.icon;
                return {
                  value: ct.id,
                  label: ct.name, 
                  icon: <IconComponent className="h-5 w-5" />
                };
              })}
              value={previewChartType}
              onChange={(newType) => setPreviewChartType(newType as ChartType)}
              className="w-full"
            />
          </div>

          {/* Chart description removed for more vertical space */}

          <div className="flex-1 border border-border rounded-lg p-4 bg-muted/20 min-h-[400px] overflow-hidden flex flex-col">
            {viewMode === 'chart' ? (
              <div className="flex-1 min-h-0">
                <UniversalChartRenderer chart={chartToRenderForPreview} />
              </div>
            ) : (
              <div className="h-full overflow-hidden flex flex-col">
                {/* Simple header with just row count */}
                <div className="flex items-center justify-between mb-3 flex-shrink-0">
                  <h3 className="text-lg font-semibold">Data Table</h3>
                  <span className="text-sm text-gray-600">
                    {(() => {
                      const dataLength = chart.data ? chart.data.length : 0;
                      return `${dataLength} rows`;
                    })()}
                  </span>
                </div>
                
                <div className="flex-1 overflow-hidden">
                  <DataTableViewer
                    initialData={chart.data || []}
                    showHeaderOnly={false}
                    showTableOnly={true}
                    className="h-full compact-rows"
                    enableSmartFormatting={false}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Chart Information section removed */}
          
          {/* Bottom Right Buttons */}
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => 
              onApplyStudies(
                studies, 
                previewChartType, 
                previewAxisOptions, 
                { showGridLines: previewShowGridLines }, 
                chartToRenderForPreview.configuration || {}
              )
            }>Apply</Button>
          </div>
        </div> {/* Closes Right Panel div that starts with <div className="flex-1 p-6 flex flex-col overflow-y-auto"> */}

        <ChartAxisSettingsDialog 
          isOpen={isAxisSettingsDialogOpen} 
          onClose={() => setIsAxisSettingsDialogOpen(false)}
          chartDataForPreview={chartToRenderForPreview} // Pass the potentially modified chart object
          initialAxisOptions={previewAxisOptions} // Pass current preview options
          onSaveAxisOptions={(newOptions) => {
            setPreviewAxisOptions(newOptions); 
            setIsAxisSettingsDialogOpen(false);
          }}
        />

        {/* Data Editor Dialog */}
        {isDataEditorDialogOpen && isDatabaseChart && (
          <ChartDataEditorDialog
            isOpen={isDataEditorDialogOpen}
            onClose={() => setIsDataEditorDialogOpen(false)}
            chart={chart as FullChart}
            onChartUpdate={() => {
              if (onChartUpdate) {
                onChartUpdate();
              }
              setIsDataEditorDialogOpen(false);
            }}
          />
        )}

      </div> {/* Closes Main Dialog Content container <div className="bg-white dark:bg-gray-800 ..."> */}
    </div> /* Closes Dialog Overlay/Wrapper <div className="fixed inset-0 ..."> */
  );
};

export default ChartStudiesDialog;

// Add global styles for compact table rows
const style = document.createElement('style');
style.textContent = `
  .compact-rows td {
    padding-top: 6px !important;
    padding-bottom: 6px !important;
  }
  .compact-rows th {
    padding-top: 8px !important;
    padding-bottom: 8px !important;
  }
`;
if (!document.head.querySelector('[data-compact-rows]')) {
  style.setAttribute('data-compact-rows', 'true');
  document.head.appendChild(style);
} 